import { useState } from 'react';
import { motion } from 'framer-motion';
import { Camera, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { nexaApi } from './api';

const _resizeToBase64 = (file, maxEdge, quality) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL('image/jpeg', quality).split(',')[1]);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

// Progressive re-compression so 3 photos always fit under the ingress limit.
// Base64 inflates ~33%; we target ≤ 2.8 MB total base64 (≈ 3.7 MB JSON body).
const MAX_TOTAL_B64 = 2_800_000;
const compressAll = async (fileList) => {
  const passes = [
    { edge: 1280, q: 0.82 },
    { edge: 1100, q: 0.78 },
    { edge: 960,  q: 0.72 },
    { edge: 820,  q: 0.66 },
    { edge: 720,  q: 0.60 },
  ];
  let last = [];
  for (const { edge, q } of passes) {
    last = await Promise.all(fileList.map((f) => _resizeToBase64(f, edge, q)));
    const total = last.reduce((n, s) => n + s.length, 0);
    if (total <= MAX_TOTAL_B64) return last;
  }
  return last; // last-ditch smallest pass
};

export const AddItemModal = ({ onClose, onAdded }) => {
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    setFiles((prev) => [
      ...prev,
      ...incoming.slice(0, 3 - prev.length).map((file) => ({
        id: `${Date.now()}-${Math.random()}`,
        file,
        url: URL.createObjectURL(file),
      })),
    ]);
  };

  const submit = async () => {
    if (busy || (!files.length && !note.trim())) return;
    setBusy(true);
    try {
      const images_base64 = await compressAll(files.map((f) => f.file));
      const { data } = await nexaApi.post('/wardrobe/add', {
        images_base64,
        note: note.trim() || null,
      });
      toast.success(`Added — ${data.name}`);
      onAdded();
      onClose();
    } catch (e) {
      const msg = e?.response?.status === 413
        ? 'Photos are too large — remove one and try again'
        : (typeof e?.response?.data?.detail === 'string' && e.response.data.detail) || 'Could not add the item';
      toast.error(msg);
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      data-testid="nexa-add-item-modal"
      onClick={(e) => e.target === e.currentTarget && !busy && onClose()}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0A0A0A] p-6"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-light nexa-heading">Add item</h3>
          <button onClick={onClose} disabled={busy} aria-label="Close" data-testid="nexa-add-item-close"
            className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/60">
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <div className="flex gap-2 flex-wrap mb-4">
          {files.map((f) => (
            <div key={f.id} className="relative">
              <img src={f.url} alt="" className="w-16 h-16 rounded-xl object-cover border border-white/10" />
              <button
                onClick={() => setFiles((prev) => prev.filter((x) => x.id !== f.id))}
                aria-label="Remove photo"
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black border border-white/25 flex items-center justify-center text-white/80"
              >
                <X className="w-3 h-3" strokeWidth={2.5} />
              </button>
            </div>
          ))}
          {files.length < 3 && (
            <label className="w-16 h-16 rounded-xl border border-dashed border-white/20 flex flex-col items-center justify-center gap-1 text-white/40 cursor-pointer active:scale-95 transition-transform"
              data-testid="nexa-add-item-photo">
              <Camera className="w-4 h-4" strokeWidth={1.8} />
              <span className="text-[9px] font-mono">{files.length}/3</span>
              <input type="file" accept="image/*" multiple className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
            </label>
          )}
        </div>
        <p className="text-[11px] text-white/35 mb-4">
          Photos of the real item give the most accurate result — NEXA studies the exact fabric, cut and details.
        </p>

        <textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional name / details — e.g. 'Emporio Armani sunglasses', brand, fit…"
          data-testid="nexa-add-item-note"
          className="nexa-input w-full rounded-xl px-4 py-3 text-sm resize-none mb-4"
        />

        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={submit}
          disabled={busy || (!files.length && !note.trim())}
          data-testid="nexa-add-item-submit"
          className="w-full h-12 rounded-xl bg-white text-black font-semibold text-sm disabled:opacity-30 flex items-center justify-center gap-2"
        >
          {busy ? (
            <>
              <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              Analyzing item…
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" strokeWidth={2.5} />
              Add to wardrobe
            </>
          )}
        </motion.button>
      </motion.div>
    </motion.div>
  );
};
